import React, { ComponentProps, ReactNode } from "react";
import { Animated, Keyboard, Platform } from "react-native";
import {
  Check,
  ChevronDown as LChevronDown,
  ChevronUp as LChevronUp,
} from "@tamagui/lucide-icons";
import {
  GetThemeValueForKey,
  styled,
  Text,
  Select as TGSelect,
  useSelectContext,
  View,
  XStack,
  YStack,
  type SelectProps,
} from "tamagui";

import { withIconRef } from "../../utils";

const Trigger = styled(TGSelect.Trigger, {
  name: "Trigger",
  width: "100%",
  flexShrink: 1,
  variants: {
    variant: {
      default: {
        borderBottomColor: "$gray-secondary-400",
        borderTopWidth: 0,
        borderLeftWidth: 0,
        borderRightWidth: 0,
        borderBottomWidth: "$0.2",
        backgroundColor: "transparent",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-end",
        hoverStyle: {
          borderBottomColor: "black",
        },
        focusStyle: {
          borderBottomColor: "$lightblue-700",
        },
        disabledStyle: {
          borderBottomColor: "$gray-200",
          cursor: "not-allowed",
        },
      },
    },
    size: {
      md: {
        paddingBottom: "$1.2",
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: "$0.3",
      },
    },
  } as const,
  defaultVariants: {
    variant: "default",
    size: "md",
  },
});

const SelectedLabel = styled(Text, {
  name: "SelectedLabel",
  width: "100%",
  ellipsizeMode: "tail",
  numberOfLines: 1,
  variants: {
    variant: {
      default: {
        color: "$gray-primary-900",
        ellipsizeMode: "tail",
        marginBottom: "$0.5",
        disabledStyle: {
          color: "$gray-200",
          cursor: "not-allowed",
        },
      },
    },
    font: {
      md: {
        fontFamily: "$body",
        fontSize: "$3",
        lineHeight: "$3",
        fontWeight: "$5",
      },
    },
  } as const,

  defaultVariants: {
    variant: "default",
    font: "md",
  },
});

const ChevronDown = styled(withIconRef(LChevronDown), {
  name: "ChevronDown",
  variants: {
    variant: {
      default: {
        color: "$gray-secondary-400",
        paddingBottom: "$0.3",
      },
    },
    size: {
      md: {
        size: "$1",
      },
    },
  } as const,
  defaultVariants: {
    variant: "default",
    size: "md",
  },
});

const ChevronUp = styled(withIconRef(LChevronUp), {
  name: "ChevronUp",
  variants: {
    variant: {
      default: {
        color: "$gray-secondary-400",
        paddingBottom: "$0.3",
      },
    },
    size: {
      md: {
        size: "$1",
      },
    },
  } as const,
  defaultVariants: {
    variant: "default",
    size: "md",
  },
});

const Viewport = styled(TGSelect.Viewport, {
  name: "Viewport",
  variants: {
    variant: {
      default: {
        borderColor: "$gray-200",
        borderWidth: "$0.2",
      },
    },
    size: {
      md: {
        maxHeight: 300,
        minWidth: 200,
      },
    },
    rounded: {
      md: {
        borderRadius: "$2",
      },
    },
  } as const,
  defaultVariants: {
    variant: "default",
    size: "md",
    rounded: "md",
  },
});

const SELECT_GROUP_PADDING = 16;

const SelectGroup = styled(TGSelect.Group, {
  name: "SelectGroup",
  variants: {
    variant: {
      default: {
        backgroundColor: "white",
      },
    },
    size: {
      md: {
        padding: "$2",
      },
    },
  } as const,
  defaultVariants: {
    variant: "default",
    size: "md",
  },
});

const ItemTextBase = styled(Text, {
  name: "ItemTextBase",
  variants: {
    variant: {
      default: {
        fontFamily: "$subtitle",
        fontSize: "$2",
        fontWeight: "$4",
        lineHeight: "$2",

        disabledStyle: {
          color: "$gray-200",
          cursor: "not-allowed",
        },
      },
    },
    selected: {
      true: {
        color: "$lightblue-700",
      },
      false: {
        color: "$gray-primary-900",
      },
    },
  } as const,
  defaultVariants: {
    variant: "default",
  },
});

const CheckIcon = styled(withIconRef(Check), {
  name: "CheckIcon",
  variants: {
    variant: {
      default: {},
    },
    selected: {
      true: {
        color: "$lightblue-700",
      },
      false: {
        color: "$gray-primary-900",
      },
    },
  } as const,
  defaultVariants: {
    variant: "default",
  },
});

type ItemTextBaseProps = ComponentProps<typeof ItemTextBase>;

const ItemText: React.FC<ItemTextBaseProps> = ({ children, ...rest }) => {
  return (
    <TGSelect.ItemText>
      <ItemTextBase {...rest}>{children}</ItemTextBase>
    </TGSelect.ItemText>
  );
};

type ItemBaseProps = ComponentProps<typeof TGSelect.Item>;

const ITEM_INDICATOR_NAME = "SelectItemIndicator";

const Item: React.FC<
  Omit<ItemBaseProps, "children"> & {
    children: (selected: boolean, open: boolean) => ReactNode;
  }
> = ({ children, value, ...rest }) => {
  const itemContext = useSelectContext(ITEM_INDICATOR_NAME, undefined);
  return (
    <TGSelect.Item
      paddingHorizontal={0}
      paddingVertical={"$0.5"}
      gap={"$1.5"}
      value={value}
      {...rest}
    >
      {children(itemContext.value === value, itemContext.open)}
    </TGSelect.Item>
  );
};

export const Select: React.FC<
  Omit<SelectProps, "onValueChange"> & {
    items: { name: string; value: string }[];
    onValueChange?: (val: string) => void;
    trigger?: string | ((selected: typeof TGSelect.Value) => ReactNode);
    label?: string;
    defaultValue?: string;
    disabled?: boolean;
    dataId?: string;
    maxWidth?:
      | number
      | "unset"
      | Animated.AnimatedNode
      | GetThemeValueForKey<"maxWidth">
      | null
      | undefined;
  }
> = ({
  items,
  onValueChange,
  maxWidth,
  trigger,
  label,
  defaultValue,
  disabled,
  dataId,
  ...rest
}) => {
  const [width, setWidth] = React.useState<number>(0);

  const handleOpen = () => {
    Keyboard.dismiss();
    if (
      Platform.OS === "web" &&
      document.activeElement instanceof HTMLElement
    ) {
      document.activeElement.blur();
    }
  };

  return (
    <XStack
      flex={1}
      onLayout={(event) => setWidth(event.nativeEvent.layout.width)}
      maxWidth={maxWidth}
      data-id={dataId}
    >
      <TGSelect
        onOpenChange={handleOpen}
        onValueChange={onValueChange}
        disablePreventBodyScroll={false}
        defaultValue={defaultValue}
        {...rest}
      >
        {typeof trigger === "string" ? (
          <Trigger
            data-id={"select-trigger"}
            disabled={disabled}
            iconAfter={ChevronDown}
          >
            <View alignItems="flex-start">
              <SelectedLabel
                data-id={"select-label"}
                maxWidth={width}
                disabled={disabled}
              >
                {label}
              </SelectedLabel>
              <TGSelect.Value
                data-id={"select-value"}
                maxWidth={width}
                flex={1}
                alignSelf="stretch"
                flexShrink={1}
                color="$gray-primary-900"
                ellipsizeMode="tail"
                fontFamily="$subtitle"
                fontSize="$2"
                fontWeight="$4"
                lineHeight="$2"
                disabledStyle={{
                  color: "$gray-200",
                  cursor: "not-allowed",
                }}
                placeholder={trigger}
                disabled={disabled}
              />
            </View>
          </Trigger>
        ) : (
          <TGSelect.Trigger tag="div" padding={0} unstyled>
            {trigger?.(TGSelect.Value)}
          </TGSelect.Trigger>
        )}

        <TGSelect.Content zIndex={200000}>
          <TGSelect.ScrollUpButton
            alignItems="center"
            justifyContent="center"
            position="relative"
            width="100%"
            height="$3"
          >
            <YStack zIndex={10}>
              <ChevronUp />
            </YStack>
          </TGSelect.ScrollUpButton>

          <Viewport
            animation="medium"
            animateOnly={["opacity"]}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          >
            <SelectGroup data-id={"select-group"}>
              {React.useMemo(
                () =>
                  items.map((item, i) => {
                    return (
                      <Item
                        data-id={"select-item"}
                        cursor="pointer"
                        data-value={item.value}
                        data-name={item.name}
                        width={width - SELECT_GROUP_PADDING * 2}
                        index={i}
                        key={item.value}
                        value={item.value}
                      >
                        {(selected, open) => (
                          <>
                            <ItemText
                              data-id={"select-text"}
                              data-value={item.value}
                              data-name={item.name}
                              disabled={disabled}
                              selected={open ? selected : false}
                            >
                              {item.name}
                            </ItemText>
                            <TGSelect.ItemIndicator marginLeft="auto">
                              <CheckIcon selected={selected} size={16} />
                            </TGSelect.ItemIndicator>
                          </>
                        )}
                      </Item>
                    );
                  }),
                [disabled, items, width],
              )}
            </SelectGroup>
          </Viewport>

          <TGSelect.ScrollDownButton
            alignItems="center"
            justifyContent="center"
            position="relative"
            width="100%"
            height="$3"
          >
            <YStack zIndex={10}>
              <ChevronDown />
            </YStack>
          </TGSelect.ScrollDownButton>
        </TGSelect.Content>
      </TGSelect>
    </XStack>
  );
};
